import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs, deleteDoc, doc, updateDoc, addDoc } from 'firebase/firestore';
import app, { db, auth } from '../../../firebaseConfig';
import { getFunctions, httpsCallable } from 'firebase/functions';

interface SyncResult {
    success: boolean;
    results: {
        created: number;
        updated: number;
        errors: number;
        errorDetails: string[];
    };
}

interface UserData {
    id: string;
    phoneNumber: string;
    display_name: string;
    Role: string;
    postalCode?: string;
    address?: string;
    [key: string]: any;
}

export default function UserManagement() {
    const navigate = useNavigate();
    const [users, setUsers] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingUser, setEditingUser] = useState<UserData | null>(null);
    const [editForm, setEditForm] = useState<Partial<UserData>>({});
    const [isAddingUser, setIsAddingUser] = useState(false);
    const [newUserForm, setNewUserForm] = useState({
        display_name: '',
        phoneNumber: '',
        Role: 'user',
        postalCode: '',
        address: ''
    });
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<SyncResult | null>(null);

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const querySnapshot = await getDocs(collection(db, 'User'));
            const usersData = querySnapshot.docs.map((userDoc) => ({
                id: userDoc.id,
                ...userDoc.data()
            })) as UserData[];
            setUsers(usersData);
        } catch (error: any) {
            console.error('Error fetching users:', error);
            alert(`ユーザー一覧の取得に失敗しました: ${error.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (user: UserData) => {
        setEditingUser(user);
        setEditForm({ ...user });
    };

    const handleSaveEdit = async () => {
        if (!editingUser) return;

        try {
            const { id, ...updateData } = editForm;
            await updateDoc(doc(db, 'User', editingUser.id), updateData);
            alert('ユーザー情報を更新しました');
            setEditingUser(null);
            setEditForm({});
            fetchUsers();
        } catch (error) {
            console.error('Error updating user:', error);
            alert(`更新に失敗しました: ${error}`);
        }
    };

    const handleAddUser = async () => {
        if (!newUserForm.display_name || !newUserForm.phoneNumber) {
            alert('表示名と電話番号は必須です');
            return;
        }

        try {
            await addDoc(collection(db, 'User'), {
                display_name: newUserForm.display_name,
                phoneNumber: newUserForm.phoneNumber,
                Role: newUserForm.Role,
                postalCode: newUserForm.postalCode,
                address: newUserForm.address
            });

            alert('ユーザーを追加しました');
            setIsAddingUser(false);
            setNewUserForm({ display_name: '', phoneNumber: '', Role: 'user', postalCode: '', address: '' });
            fetchUsers();
        } catch (error) {
            console.error('Error adding user:', error);
            alert(`追加に失敗しました: ${error}`);
        }
    };

    const handleDelete = async (userId: string) => {
        if (!confirm('このユーザーを削除してもよろしいですか？')) return;

        try {
            await deleteDoc(doc(db, 'User', userId));
            alert('ユーザーを削除しました');
            fetchUsers();
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('削除に失敗しました');
        }
    };

    const handleLogout = async () => {
        try {
            await auth.signOut();
            navigate('/login');
        } catch (error) {
            console.error('Error signing out:', error);
        }
    };

    const searchAddress = async (zipcode: string, isEdit: boolean) => {
        if (!zipcode) return;

        try {
            const response = await fetch(`https://zipcloud.ibsnet.co.jp/api/search?zipcode=${zipcode}`);
            const data = await response.json();

            if (data.results) {
                const result = data.results[0];
                const fullAddress = `${result.address1}${result.address2}${result.address3}`;

                if (isEdit) {
                    setEditForm(prev => ({ ...prev, address: fullAddress }));
                } else {
                    setNewUserForm(prev => ({ ...prev, address: fullAddress }));
                }
            } else {
                alert('住所が見つかりませんでした');
            }
        } catch (error) {
            console.error('Error searching address:', error);
            alert('住所検索に失敗しました');
        }
    };

    const handleSync = async () => {
        if (!confirm('スプレッドシートからデータを同期しますか?\n既存のデータは上書きされます。')) return;

        setIsSyncing(true);
        try {
            const functions = getFunctions(app, 'asia-northeast1');
            const syncFunction = httpsCallable(functions, 'syncUsersFromSheet');
            const result = await syncFunction();

            const data = result.data as SyncResult;
            setSyncResult(data);
            alert(`同期完了!\n新規作成: ${data.results.created}件\n更新: ${data.results.updated}件\nエラー: ${data.results.errors}件`);

            fetchUsers();
        } catch (error: any) {
            console.error('Sync error:', error);
            alert(`同期に失敗しました: ${error.message}`);
        } finally {
            setIsSyncing(false);
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-lg">読み込み中...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 p-8">
            <div className="max-w-7xl mx-auto">
                <div className="flex justify-between items-center mb-8">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800 mb-2">ユーザー管理</h1>
                        <p className="text-gray-600">登録されているユーザーの一覧</p>
                        <p className="text-sm text-gray-500 mt-1">※ 資格の紐付けはOrder管理ページで行います</p>
                    </div>
                    <div className="flex gap-4">
                        <button
                            onClick={handleSync}
                            disabled={isSyncing}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center gap-2"
                        >
                            {isSyncing ? (
                                <>
                                    <span className="animate-spin">⟳</span>
                                    同期中...
                                </>
                            ) : (
                                <>
                                    📊 スプレッドシートから同期
                                </>
                            )}
                        </button>
                        <button
                            onClick={() => setIsAddingUser(true)}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                        >
                            ＋ ユーザー追加
                        </button>
                        <button
                            onClick={() => navigate('/admin/dashboard')}
                            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                        >
                            ダッシュボードに戻る
                        </button>
                        <button
                            onClick={handleLogout}
                            className="px-4 py-2 bg-white border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                            ログアウト
                        </button>
                    </div>
                </div>

                <div className="mb-4 text-sm text-gray-600 font-medium">
                    合計: {users.length} ユーザー
                </div>

                <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-100 border-b">
                                <tr>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">ID</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">表示名</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">電話番号</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">権限</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">郵便番号</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">住所</th>
                                    <th className="px-6 py-4 text-left text-sm font-semibold text-gray-700">操作</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-200">
                                {users.map((user) => (
                                    <tr key={user.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 text-sm text-gray-600 font-mono text-xs">{user.id}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{user.display_name || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{user.phoneNumber || '-'}</td>
                                        <td className="px-6 py-4">
                                            <span className={`px-2 py-1 text-xs rounded-full ${user.Role === 'admin'
                                                ? 'bg-purple-100 text-purple-700'
                                                : 'bg-blue-100 text-blue-700'
                                                }`}>
                                                {user.Role || 'user'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{user.postalCode || '-'}</td>
                                        <td className="px-6 py-4 text-sm text-gray-800">{user.address || '-'}</td>
                                        <td className="px-6 py-4">
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => handleEdit(user)}
                                                    className="px-3 py-1 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 transition-colors"
                                                >
                                                    編集
                                                </button>
                                                <button
                                                    onClick={() => handleDelete(user.id)}
                                                    className="px-3 py-1 bg-red-500 text-white text-sm rounded hover:bg-red-600 transition-colors"
                                                >
                                                    削除
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {users.length === 0 && (
                        <div className="text-center py-12 text-gray-500">
                            ユーザーが登録されていません
                        </div>
                    )}
                </div>

                {/* Sync Result Modal */}
                {syncResult && (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                        <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
                            <h2 className="text-2xl font-bold text-gray-800 mb-4">同期結果</h2>
                            <div className="space-y-3">
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">新規作成:</span>
                                    <span className="font-bold text-green-600">{syncResult.results.created}件</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">更新:</span>
                                    <span className="font-bold text-blue-600">{syncResult.results.updated}件</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-700">エラー:</span>
                                    <span className="font-bold text-red-600">{syncResult.results.errors}件</span>
                                </div>
                                {syncResult.results.errorDetails && syncResult.results.errorDetails.length > 0 && (
                                    <div className="mt-4">
                                        <p className="text-sm font-medium text-gray-700 mb-2">エラー詳細:</p>
                                        <div className="bg-red-50 border border-red-200 rounded p-3 max-h-40 overflow-y-auto">
                                            {syncResult.results.errorDetails.map((error: string, index: number) => (
                                                <p key={index} className="text-sm text-red-700">{error}</p>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <button
                                onClick={() => setSyncResult(null)}
                                className="mt-6 w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                閉じる
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Edit Modal */}
            {editingUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">ユーザー編集</h2>
                            <p className="text-sm text-gray-600 mt-1">ID: {editingUser.id}</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">表示名</label>
                                <input
                                    type="text"
                                    value={editForm.display_name || ''}
                                    onChange={(e) => setEditForm({ ...editForm, display_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">電話番号</label>
                                <input
                                    type="text"
                                    value={editForm.phoneNumber || ''}
                                    onChange={(e) => setEditForm({ ...editForm, phoneNumber: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">郵便番号</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={editForm.postalCode || ''}
                                            onChange={(e) => setEditForm({ ...editForm, postalCode: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="1234567"
                                        />
                                        <button
                                            onClick={() => searchAddress(editForm.postalCode || '', true)}
                                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                                        >
                                            検索
                                        </button>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">住所</label>
                                    <input
                                        type="text"
                                        value={editForm.address || ''}
                                        onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">権限</label>
                                <select
                                    value={editForm.Role || 'user'}
                                    onChange={(e) => setEditForm({ ...editForm, Role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setEditingUser(null);
                                    setEditForm({});
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleSaveEdit}
                                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                            >
                                保存
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add User Modal */}
            {isAddingUser && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
                    <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b">
                            <h2 className="text-2xl font-bold text-gray-800">ユーザー追加</h2>
                            <p className="text-sm text-gray-600 mt-1">新しいユーザーを登録します</p>
                        </div>

                        <div className="p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    表示名 <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newUserForm.display_name}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, display_name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="山田太郎"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    電話番号 <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={newUserForm.phoneNumber}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, phoneNumber: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    placeholder="+819012345678"
                                />
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">郵便番号</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="text"
                                            value={newUserForm.postalCode}
                                            onChange={(e) => setNewUserForm({ ...newUserForm, postalCode: e.target.value })}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            placeholder="1234567"
                                        />
                                        <button
                                            onClick={() => searchAddress(newUserForm.postalCode, false)}
                                            className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                                        >
                                            検索
                                        </button>
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">住所</label>
                                    <input
                                        type="text"
                                        value={newUserForm.address}
                                        onChange={(e) => setNewUserForm({ ...newUserForm, address: e.target.value })}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">権限</label>
                                <select
                                    value={newUserForm.Role}
                                    onChange={(e) => setNewUserForm({ ...newUserForm, Role: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                >
                                    <option value="user">user</option>
                                    <option value="admin">admin</option>
                                </select>
                            </div>
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setIsAddingUser(false);
                                    setNewUserForm({ display_name: '', phoneNumber: '', Role: 'user', postalCode: '', address: '' });
                                }}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                キャンセル
                            </button>
                            <button
                                onClick={handleAddUser}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                                追加
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
